import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getOrderDetail } from "@/api/staff";
import type { StaffOrderDetail } from "@/api/staff";

const BRAND = "#FF6B35";

function ImageGrid({ imgs }: { imgs: string[] }) {
  return (
    <div className="grid grid-cols-4 gap-2 mt-3">
      {imgs.map((url, idx) => (
        <button
          key={idx}
          type="button"
          className="relative w-full rounded-xl overflow-hidden bg-gray-100"
          style={{ paddingTop: "100%" }}
          onClick={() => window.open(url, "_blank")}
        >
          <img
            src={url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        </button>
      ))}
    </div>
  );
}

function Block({
  title,
  timeDesc,
  imgs,
  remark,
  emptyTitle,
}: {
  title: string;
  timeDesc?: string;
  imgs?: string[];
  remark?: string;
  emptyTitle: string;
}) {
  const has = !!timeDesc;
  return (
    <div className="li mb-6">
      {has ? (
        <>
          <div className="flex items-start justify-between gap-3 w-full min-w-0">
            <div className="flex-1 min-w-0 text-base font-bold text-gray-800 truncate">
              {title}
            </div>
            <div className="max-w-[52%] shrink-0 pr-4 text-right text-xs text-gray-500 leading-snug break-words">
              {timeDesc || ""}
            </div>
          </div>
          {!!imgs?.length && <ImageGrid imgs={imgs} />}
          <div className="mt-2 text-xs text-gray-800">
            备注信息：{remark || ""}
          </div>
        </>
      ) : (
        <div className="flex items-start justify-between w-full min-w-0">
          <div className="flex-1 min-w-0 text-base font-bold text-gray-500 truncate">
            {emptyTitle}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkspacePunchingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = Number(searchParams.get("id") || 0);

  const [orderInfo, setOrderInfo] = useState<StaffOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    getOrderDetail(orderId)
      .then(setOrderInfo)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading || !orderInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <i className="ri-loader-4-line text-2xl animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      {/* Header */}
      <div style={{ background: BRAND }}>
        <div
          className="px-5 flex items-center gap-3"
          style={{
            paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)",
            paddingBottom: 13,
          }}
        >
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0"
            style={{ background: "rgba(255,255,255,0.2)" }}
          >
            <i
              className="ri-arrow-left-line"
              style={{ fontSize: 20, color: "#fff" }}
            />
          </button>
          <span className="text-lg font-bold text-white">打卡记录</span>
        </div>
      </div>

      <div className="px-4 py-4">
        <div className="main bg-white rounded-2xl p-4">
          <Block
            title="服务开始打卡"
            timeDesc={orderInfo.sign_time_desc}
            imgs={orderInfo.sign_imgs}
            remark={orderInfo.sign_content}
            emptyTitle="暂无服务开始打卡信息"
          />

          <Block
            title="服务完成打卡"
            timeDesc={orderInfo.finish_time_desc}
            imgs={orderInfo.staff_imgs}
            remark={orderInfo.staff_remark}
            emptyTitle="暂无服务完成打卡信息"
          />
        </div>
      </div>

      {/* 简化的样式：用内联模拟 vue 的 li 竖线效果 */}
      <style>{`
        .main .li{
          width: 100%;
          box-sizing: border-box;
          overflow-x: hidden;
          padding-bottom: 24px;
          padding-left: 28px;
          position: relative;
        }
        .main .li:before{
          content: "";
          display: block;
          box-sizing: border-box;
          width: 14px;
          height: 14px;
          position: absolute;
          left: 0;
          top: 4px;
          border: 3px solid rgba(232,247,242,1);
          border-radius: 50%;
          background-color: #1ab27b;
          z-index: 2;
        }
        .main .li:after{
          content: "";
          display: block;
          width: 0;
          position: absolute;
          left: 7px;
          top: 16px;
          bottom: 0;
          border-left: 1px dashed #d9d9d9;
          z-index: 1;
        }
        .main .li:last-child{
          padding-bottom: 0;
        }
        .main .li:last-child:after{
          display: none;
        }
      `}</style>
    </div>
  );
}
